import { SnoozeSheet } from "./SnoozeSheet";
import {
  useEquipmentSnooze,
  useCreateEquipmentSnooze,
  useDeleteEquipmentSnooze,
  useLocationSnooze,
} from "@/hooks/useSnooze";
import { toast } from "sonner";

type EquipmentSnoozeSheetWrapperProps = {
  equipmentId: number;
  equipmentName: string;
  isSnoozed: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When provided, fetches the location snooze so the manage step can show it */
  locationId?: number;
  locationSnoozed?: boolean;
};

export const EquipmentSnoozeSheetWrapper = ({
  equipmentId,
  equipmentName,
  isSnoozed,
  open,
  onOpenChange,
  locationId,
  locationSnoozed = false,
}: EquipmentSnoozeSheetWrapperProps) => {
  const { data: equipmentSnooze, isLoading: isEquipmentSnoozeLoading } = useEquipmentSnooze(equipmentId, isSnoozed);

  // Also fetch location snooze when the equipment is effectively snoozed via its location
  const shouldFetchLocationSnooze = isSnoozed && !equipmentSnooze && !isEquipmentSnoozeLoading && !!locationId && locationSnoozed;
  const { data: locationSnooze, isLoading: isLocationSnoozeLoading } = useLocationSnooze(locationId ?? 0, shouldFetchLocationSnooze);

  const createSnooze = useCreateEquipmentSnooze();
  const deleteEquipmentSnooze = useDeleteEquipmentSnooze();

  // The active snooze is either the direct equipment snooze or the inherited location snooze
  const activeSnooze = equipmentSnooze ?? (shouldFetchLocationSnooze ? locationSnooze : null);
  const isSnoozeLoading = isEquipmentSnoozeLoading || (shouldFetchLocationSnooze && isLocationSnoozeLoading);
  const isLocationLevel = !equipmentSnooze && !!locationSnooze && shouldFetchLocationSnooze;

  // Don't open the sheet until snooze data is loaded for snoozed items
  // so the manage step shows immediately instead of flashing the duration step
  const effectiveOpen = open && (!isSnoozed || !isSnoozeLoading);

  const handleSnooze = (end: string | null, reason: string | null) => {
    createSnooze.mutate(
      { equipmentId, end, reason },
      {
        onSuccess: () => {
          onOpenChange(false);
          toast.success("Notifications snoozed");
        },
        onError: () => toast.error("Failed to snooze"),
      }
    );
  };

  const handleLift = () => {
    if (!activeSnooze) return;
    deleteEquipmentSnooze.mutate(
      { equipmentId, snoozeId: activeSnooze.id },
      {
        onSuccess: () => {
          onOpenChange(false);
          toast.success("Snooze lifted");
        },
        onError: () => toast.error("Failed to lift snooze"),
      }
    );
  };

  return (
    <SnoozeSheet
      open={effectiveOpen}
      onOpenChange={onOpenChange}
      targetName={equipmentName}
      onSnooze={handleSnooze}
      isPending={createSnooze.isPending}
      onLift={handleLift}
      isLiftPending={deleteEquipmentSnooze.isPending}
      activeSnooze={isSnoozed ? activeSnooze : null}
      locationLevelMessage={
        isLocationLevel
          ? "This snooze is from the location. Please lift the snooze from the location instead."
          : null
      }
    />
  );
};
